import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Trophy, LayoutDashboard, History, BarChart3, Sun, Moon, LogOut, Menu, User, Coins, Gamepad2, Heart } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export const ClientNavbar = () => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const navLinks = [
        { href: '/profile', label: 'Mi Perfil', icon: User },
        { href: '/dashboard', label: 'Eventos', icon: LayoutDashboard },
        { href: '/history', label: 'Historial', icon: History },
        { href: '/ranking', label: 'Ranking', icon: BarChart3 },
        { href: '/game', label: 'Juegos', icon: Gamepad2 },
    ];

    const isActive = (path) => location.pathname === path;

    // ─── DESKTOP SIDEBAR ────────────────────────────────────────────────────────
    const SidebarContent = ({ onNavClick }) => (
        <div className="flex flex-col h-full py-8 px-4 gap-y-6">

            {/* Brand Logo */}
            <div className="px-2 mb-4">
                <Link to="/dashboard" onClick={onNavClick}>
                    <span className="text-lg font-black tracking-tighter text-[#001944] dark:text-white uppercase leading-tight">
                        POLLA<br />REMAKIA
                    </span>
                </Link>
            </div>

            {/* User Profile Card */}
            <div
                className="flex items-center gap-3 px-3 py-4 mb-2
                    bg-slate-100 dark:bg-[#122550] rounded-xl
                    transition-colors"
            >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 dark:bg-white/20 shrink-0">
                    <Avatar className="w-10 h-10">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-[#001944] text-white font-bold text-sm">
                            {user?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                    </Avatar>
                </div>
                <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-bold text-[#191c1d] dark:text-white truncate">
                        {user?.name || 'Usuario'}
                    </span>
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex items-center gap-1.5">
                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                <Coins size={10} strokeWidth={2.5} />
                                <span className="text-[10px] font-bold leading-none">
                                    {user?.credits || 0}
                                </span>
                            </div>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mr-1">
                                Créditos
                            </span>
                        </div>

                        {(user?.game_lives > 0) && (
                            <div className="flex items-center gap-1.5">
                                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                                    <Heart size={10} strokeWidth={2.5} />
                                    <span className="text-[10px] font-bold leading-none">
                                        {user?.game_lives || 0}
                                    </span>
                                </div>
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                                    Vidas
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Navigation */}
            <nav className="flex-1 space-y-1">
                {navLinks.map((link) => {
                    const active = isActive(link.href);
                    return (
                        <Link
                            key={link.href}
                            to={link.href}
                            onClick={onNavClick}
                            className={`
                                flex items-center gap-3 px-4 py-3 text-sm font-medium
                                transition-all duration-150
                                ${active
                                    ? 'text-[#001944] dark:text-white font-bold border-r-4 border-[#4c56af] bg-slate-200/50 dark:bg-white/5'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-[#4c56af] hover:bg-slate-100 dark:hover:bg-white/10'
                                }
                            `}
                        >
                            <link.icon size={18} strokeWidth={active ? 2.5 : 1.75} />
                            {link.label}
                        </Link>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="pt-6 border-t border-slate-200 dark:border-white/10 space-y-1">
                <button
                    onClick={() => { toggleTheme(); onNavClick?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium
                        text-slate-500 dark:text-slate-400
                        hover:text-[#4c56af] hover:bg-slate-100 dark:hover:bg-white/10
                        transition-all text-left"
                >
                    {theme === 'dark' ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
                    {theme === 'dark' ? 'Tema Claro' : 'Tema Oscuro'}
                </button>
                <button
                    onClick={() => logout()}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium
                        text-slate-500 dark:text-slate-400
                        hover:text-red-500 hover:bg-slate-100 dark:hover:bg-white/10
                        transition-all text-left"
                >
                    <LogOut size={18} strokeWidth={1.75} />
                    Logout
                </button>
            </div>
        </div>
    );

    return (
        <div className="contents">
            {/* ── DESKTOP SIDEBAR ── */}
            <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col z-50
                bg-slate-50 dark:bg-[#001944]
                shadow-[4px_0px_16px_rgba(0,0,0,0.04)]">
                <SidebarContent />
            </aside>

            {/* ── MOBILE HEADER ── */}
            <nav className="md:hidden sticky top-0 z-50 border-b border-border/50
                bg-slate-50/90 dark:bg-[#001944]/90 backdrop-blur-md">
                <div className="flex justify-between items-center h-14 px-4">

                    {/* Logo */}
                    <Link to="/dashboard" className="text-base font-black tracking-tighter text-[#001944] dark:text-white uppercase leading-none">
                        POLLA REMAKIA
                    </Link>

                    {/* Hamburger */}
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button className="p-2 text-slate-500 dark:text-slate-400 hover:text-[#4c56af] transition-colors">
                                <Menu size={22} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-64 p-0 bg-slate-50 dark:bg-[#001944] border-l border-slate-200 dark:border-white/10">
                            <SidebarContent onNavClick={() => setMobileOpen(false)} />
                        </SheetContent>
                    </Sheet>
                </div>
            </nav>
        </div>
    );
};

export default ClientNavbar;
